import {
  Button,
  Drawer,
  Input,
  LegacyForm,
  QRCode,
  Space,
  Typography,
  useMessagedAsyncFn,
  useMessagedAsyncRetry,
} from "@payping/web-components";
import { Close, GoogleLogo, Key, Password } from "@payping/icons";
import { useEffect, useState } from "react";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { accountApis } from "@payping/api";
import { useNavigate } from "react-router-dom";
import { DashboardRoutes } from "@/router/constant";
import LittleActionDrawer from "@/components/mobile/LittleActionDrawer";

interface FormType {
  googleAuthCode: string;
}

const MobileDisableLogin2Fa = () => {
  const { profile } = DashboardRoutes;
  const navigate = useNavigate();

  const [qrCode, setQrCode] = useState<string | undefined>();

  const [showEnableQrDrawer, setShowEnableQrDrawer] = useState<boolean>(true);
  const [showDisableCodeDrawer, setShowDisableCodeDrawer] = useState<boolean>(false);

  const { t } = useAppTranslation();
  const [form] = LegacyForm.useForm<FormType>();

  const { retry: retryIsTwoFAActive } = useMessagedAsyncRetry(
    async () => {
      const state = await accountApis.getTwoFactorAuthenticationStatusPut();
      return state;
    },
    [],
    { useProgress: true },
  );

  useEffect(() => {
    const getQrCodeUrl = async () => {
      const res = await accountApis.resetAuthenticatorKeyGet();
      setQrCode(res.barcodeUrl);
    };
    getQrCodeUrl();
  }, [qrCode]);

  const [{ loading: loadingDisable }, handleDisable] = useMessagedAsyncFn(
    async (values: FormType) => {
      await accountApis.disableAuthenticatorWithCodePost(values.googleAuthCode);
      retryIsTwoFAActive();
      form.resetFields();
      setShowDisableCodeDrawer(false);
    },
    [form, retryIsTwoFAActive],
  );

  return (
    <>
      {/*  <Show Qr Code Drawer> */}
      <Drawer
        title={t("user.2fa.modal.activate2fa")}
        open={showEnableQrDrawer}
        size={"100%"}
        placement={"bottom"}
        closeIcon={false}
        extra={
          <Button
            type={"text"}
            icon={<Close width={14} height={14} />}
            onClick={() => {
              navigate(profile.m2fa.menu.url);
            }}
          />
        }
      >
        <Space orientation={"vertical"} className={"tw:w-full"} size={33}>
          <Space size={"large"}>
            <Password className={"color--gray500"} width={"64px"} />
            <GoogleLogo className={"color--gray500"} width={"64px"} />
          </Space>
          <Typography.Text type={"secondary"}>{t("user.2fa.modal.googleAuthDesc")}</Typography.Text>
          <QRCode size={200} status={qrCode === undefined ? "loading" : undefined} value={qrCode || ""} />
        </Space>
      </Drawer>

      <LittleActionDrawer
        open={showEnableQrDrawer}
        submit={
          <Button
            className={"tw:w-full"}
            loading={!!qrCode}
            type={"primary"}
            onClick={() => {
              setShowEnableQrDrawer(false);
              setShowDisableCodeDrawer(true);
            }}
          >
            {t("user.2fa.modal.nextStep")}
          </Button>
        }
        back={
          <Button
            className={"tw:w-full"}
            onClick={() => {
              setShowEnableQrDrawer(false);
              navigate(profile.m2fa.menu.url);
            }}
          >
            {t("user.2fa.modal.close")}
          </Button>
        }
      />

      {/*  </Show Qr Code Drawer> */}

      {/*  <Show Disable Code Drawer> */}
      <Drawer title={t("user.2fa.disableLoginTwoStepPassword")} open={showDisableCodeDrawer}>
        <Space size={"large"}>
          <Password className={"color--gray500"} width={"64px"} />
          <GoogleLogo className={"color--gray500"} width={"64px"} />
        </Space>
        <Space orientation={"vertical"}>
          <Typography.Text type={"secondary"}>{t("user.2fa.modal.paypingCodeDesc")}</Typography.Text>
          <LegacyForm form={form} onFinish={handleDisable} layout={"vertical"}>
            <LegacyForm.Item label={t("user.2fa.modal.code")} name="googleAuthCode" rules={[{ required: true }]}>
              <Input.Password addonBefore={<Key width={"14px"} />} />
            </LegacyForm.Item>
          </LegacyForm>
        </Space>
      </Drawer>
      <LittleActionDrawer
        submit={
          <Button loading={loadingDisable} type={"primary"} onClick={form.submit}>
            {t("common.deactivate")}
          </Button>
        }
        back={<Button onClick={() => setShowDisableCodeDrawer(false)}>{t("cancel")}</Button>}
        open={showDisableCodeDrawer}
      />

      {/*  </Show Disable Code Drawer> */}
    </>
  );
};

export default MobileDisableLogin2Fa;
